import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { MAJORS } from "../mock/data";
import I18N from "../mock/i18n";
import { BrandmarkSVG } from "./Compass";
import { LogOut, Sparkles, Check, ArrowRight, Crown, CreditCard, XCircle, Loader2 } from "lucide-react";

const PROVIDERS = [
  { id: "stripe", label: "Stripe", price: "$9.99", note: "Cards worldwide · test mode 4242 4242 4242 4242" },
  { id: "paypal", label: "PayPal", price: "$9.99", note: "PayPal balance or linked card · sandbox" },
  { id: "paystack", label: "Paystack", price: "$2.00", note: "Great for African users · test mode" },
];

export default function Dashboard() {
  const { user, loading, logout, refresh } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [runs, setRuns] = useState([]);
  const [runsErr, setRunsErr] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutErr, setCheckoutErr] = useState(null);

  // Payment return / polling state
  const [payStatus, setPayStatus] = useState(null); // "polling" | "paid" | "cancelled" | "expired" | "error"
  const [payMsg, setPayMsg] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/", { replace: true }); return; }
    if (user.is_pro) {
      api.myRuns().then((res) => setRuns(res.runs || [])).catch((e) => setRunsErr(e.data?.detail || "Failed"));
    }
  }, [user, loading, nav]);

  // On mount / URL change: detect ?session_id= (return from Stripe/PayPal/Paystack) or ?checkout=cancelled|failed
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const cancelled = params.get("checkout");
    const provider = params.get("provider"); // set by paypal/paystack return handlers
    if (cancelled === "cancelled") {
      setPayStatus("cancelled");
      setPayMsg("Checkout cancelled — no charge was made.");
      window.history.replaceState(null, "", "/dashboard");
      return;
    }
    if (cancelled === "failed") {
      setPayStatus("error");
      setPayMsg("Payment could not be completed. If money was taken, contact support.");
      window.history.replaceState(null, "", "/dashboard");
      return;
    }
    if (!sid) return;

    setPayStatus("polling");
    setPayMsg(provider === "paypal" ? "Confirming your PayPal payment…"
             : provider === "paystack" ? "Confirming your Paystack payment…"
             : "Confirming your payment with Stripe…");

    let attempts = 0;
    const maxAttempts = 8;
    const interval = 2000;

    const poll = async () => {
      attempts += 1;
      try {
        // paypal/paystack are credited by the backend redirect handler before landing here,
        // so a single genericPaymentStatus call typically returns credited=true immediately.
        const res = provider === "paypal" || provider === "paystack"
          ? await api.genericPaymentStatus(sid)
          : await api.paymentStatus(sid);
        const paid = res.payment_status === "paid" || res.credited === true;
        if (paid) {
          setPayStatus("paid");
          setPayMsg("Payment successful — welcome to Bearing Pro!");
          await refresh();
          window.history.replaceState(null, "", "/dashboard");
          return;
        }
        if (res.status === "expired") {
          setPayStatus("expired");
          setPayMsg("This checkout session expired. Please try again.");
          window.history.replaceState(null, "", "/dashboard");
          return;
        }
        if (attempts >= maxAttempts) {
          setPayStatus("error");
          setPayMsg("Payment still processing. If you were charged, refresh in a minute — otherwise try again.");
          window.history.replaceState(null, "", "/dashboard");
          return;
        }
        setTimeout(poll, interval);
      } catch (e) {
        if (attempts >= maxAttempts) {
          setPayStatus("error");
          setPayMsg(e.data?.detail || "Could not confirm payment.");
        } else {
          setTimeout(poll, interval);
        }
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const startCheckout = async (providerId) => {
    setCheckingOut(true);
    setCheckoutErr(null);
    try {
      let res;
      if (providerId === "stripe") {
        res = await api.createCheckout("pro_lifetime", window.location.origin);
      } else if (providerId === "paypal") {
        res = await api.paypalCreateOrder(window.location.origin);
      } else if (providerId === "paystack") {
        res = await api.paystackInitialize(window.location.origin);
      } else {
        throw new Error("Unknown provider");
      }
      if (res.already_pro) { await refresh(); setShowUpgrade(false); return; }
      if (!res.url) throw new Error("No checkout URL returned");
      window.location.href = res.url;
    } catch (e) {
      setCheckoutErr(e.data?.detail || e.message || "Checkout failed");
      setCheckingOut(false);
    }
  };

  if (loading) return <div className="wrap" style={{padding:80,color:"var(--white-soft)"}}>Loading…</div>;
  if (!user) return null;
  const t = I18N.en;

  return (
    <div className="App">
      <div className="topbar">
        <Link to="/" className="brandmark" style={{textDecoration:'none'}}><BrandmarkSVG /><span>{t.brand}</span></Link>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:13, color:"var(--white-soft)", opacity:.85}}>
            {user.name} {user.is_pro && <span className="badge" style={{marginLeft:8,borderColor:"var(--brass)",color:"var(--brass-bright)"}}>PRO</span>}
          </span>
          <button className="btn-secondary" onClick={logout} style={{padding:"8px 14px",fontSize:13}}><LogOut size={14} style={{verticalAlign:'middle',marginRight:6}}/>Sign out</button>
        </div>
      </div>

      <div className="wrap">
        {payStatus && (
          <div className="card" style={{
            borderColor: payStatus === "paid" ? "var(--brass)" : payStatus === "cancelled" || payStatus === "expired" || payStatus === "error" ? "var(--coral)" : "var(--line)",
          }}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              {payStatus === "polling" && <Loader2 size={22} color="var(--brass-bright)" style={{animation:"spin 1.2s linear infinite"}} />}
              {payStatus === "paid" && <Check size={22} color="var(--brass-bright)" />}
              {(payStatus === "cancelled" || payStatus === "expired" || payStatus === "error") && <XCircle size={22} color="var(--coral)" />}
              <div style={{flex:1}}>
                <div style={{fontFamily:"var(--font-display)",fontSize:20,color:"var(--parchment)"}}>
                  {payStatus === "polling" && "Confirming payment"}
                  {payStatus === "paid" && "You're now Bearing Pro"}
                  {payStatus === "cancelled" && "Checkout cancelled"}
                  {payStatus === "expired" && "Session expired"}
                  {payStatus === "error" && "Payment check timed out"}
                </div>
                <div style={{opacity:.8,fontSize:14,marginTop:4}}>{payMsg}</div>
              </div>
              <button className="btn-secondary" onClick={() => { setPayStatus(null); setPayMsg(""); }} style={{padding:"6px 12px",fontSize:12}}>Dismiss</button>
            </div>
          </div>
        )}

        {!user.is_pro && (
          <div className="card" style={{background:"linear-gradient(180deg, rgba(201,162,75,0.10), var(--ink))", borderColor:"var(--brass)"}}>
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:20,flexWrap:"wrap"}}>
              <div style={{flex:1,minWidth:280}}>
                <div className="hintline" style={{margin:0,marginBottom:8}}><Crown size={13} style={{verticalAlign:'middle',marginRight:6}}/>BEARING PRO</div>
                <h2>Unlock your permanent dashboard</h2>
                <p style={{opacity:.85,marginBottom:8}}>Save every bearing you take, revisit and re-weight past runs, and access counselor analytics for aggregate insights across your students.</p>
                <ul style={{margin:"0 0 14px 18px",padding:0,opacity:.85,fontSize:14,lineHeight:1.9}}>
                  <li><Check size={14} style={{verticalAlign:"middle",marginRight:6,color:"var(--brass-bright)"}}/>Permanent, editable run history</li>
                  <li><Check size={14} style={{verticalAlign:"middle",marginRight:6,color:"var(--brass-bright)"}}/>Counselor analytics dashboard</li>
                  <li><Check size={14} style={{verticalAlign:"middle",marginRight:6,color:"var(--brass-bright)"}}/>Claim any anonymous share link into your account</li>
                </ul>
                <button className="btn-primary" onClick={() => setShowUpgrade(true)}><Sparkles size={15} style={{verticalAlign:"middle",marginRight:6}}/>Upgrade to Pro</button>
              </div>
            </div>
          </div>
        )}

        <div className="card">
          <h2>Saved runs</h2>
          <div className="sub">{user.is_pro ? "All bearings tied to this account." : "Available on Pro. Upgrade to permanently save every run you take."}</div>
          {!user.is_pro && (
            <div className="hintline">Anonymous share links you’ve created still work — upgrade to claim them into this dashboard.</div>
          )}
          {user.is_pro && runsErr && <p style={{color:"var(--coral)"}}>{runsErr}</p>}
          {user.is_pro && !runsErr && runs.length === 0 && (
            <p style={{opacity:.7}}>No saved runs yet. <Link to="/" style={{color:"var(--brass-bright)"}}>Take your first bearing →</Link></p>
          )}
          {user.is_pro && runs.length > 0 && (
            <div style={{display:"grid",gap:10,marginTop:10}}>
              {runs.map((r) => {
                const top = (r.top_majors || [])[0];
                const majorIdx = top ? MAJORS.findIndex((m) => m.s === top.slug) : -1;
                const name = majorIdx >= 0 ? (I18N[r.lang] || I18N.en).major[majorIdx] : "—";
                const d = new Date(r.created_at);
                return (
                  <Link key={r.share_id} to={`/r/${r.share_id}`} style={{textDecoration:"none"}}>
                    <div style={{background:"var(--navy)", border:"1px solid var(--line)", borderRadius:12, padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                      <div>
                        <div style={{color:"var(--parchment)", fontWeight:600}}>{name}</div>
                        <div style={{fontSize:12, opacity:.65, fontFamily:"var(--font-mono)"}}>{d.toLocaleDateString()} · {r.profile?.name || "anonymous"} · /r/{r.share_id}</div>
                      </div>
                      <ArrowRight size={16} color="var(--brass-bright)"/>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <h2>Counselor analytics</h2>
          <div className="sub">Aggregate insights across all runs — most common majors, family-pressure trends, conflict rates, language mix.</div>
          {user.is_pro ? (
            <Link to="/counselors" className="btn-primary" style={{textDecoration:"none", display:"inline-flex", alignItems:"center", gap:8}}>
              Open counselor dashboard <ArrowRight size={14} />
            </Link>
          ) : (
            <button className="btn-primary" onClick={() => setShowUpgrade(true)}><Sparkles size={14} style={{verticalAlign:"middle",marginRight:6}}/>Upgrade to unlock</button>
          )}
        </div>
      </div>

      {showUpgrade && <UpgradeModal onClose={() => !checkingOut && setShowUpgrade(false)} onPick={startCheckout} checkingOut={checkingOut} error={checkoutErr} />}
    </div>
  );
}

function UpgradeModal({ onClose, onPick, checkingOut, error }) {
  const [tab, setTab] = useState("stripe");
  const current = PROVIDERS.find((p) => p.id === tab);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(11,24,48,0.75)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 50 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 500, margin: 0, borderColor: "var(--brass)" }}>
        <div className="hintline" style={{ margin: 0, marginBottom: 8 }}>
          <CreditCard size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
          SECURE CHECKOUT · TEST MODE
        </div>
        <h2>Bearing Pro</h2>
        <ul style={{ margin: "0 0 14px 18px", padding: 0, opacity: 0.9, fontSize: 14, lineHeight: 1.9 }}>
          <li><Check size={14} style={{ verticalAlign: "middle", marginRight: 6, color: "var(--brass-bright)" }} />Permanent, editable run history</li>
          <li><Check size={14} style={{ verticalAlign: "middle", marginRight: 6, color: "var(--brass-bright)" }} />Counselor analytics dashboard</li>
          <li><Check size={14} style={{ verticalAlign: "middle", marginRight: 6, color: "var(--brass-bright)" }} />Claim any anonymous share link into your account</li>
        </ul>

        <div style={{ display: "flex", gap: 6, marginBottom: 12, background: "var(--navy)", padding: 4, borderRadius: 10, border: "1px solid var(--line)" }}>
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setTab(p.id)}
              disabled={checkingOut}
              style={{
                flex: 1,
                padding: "9px 8px",
                borderRadius: 8,
                border: "none",
                fontSize: 13,
                fontWeight: 600,
                background: tab === p.id ? "var(--brass)" : "transparent",
                color: tab === p.id ? "var(--navy-deep)" : "var(--white-soft)",
                cursor: checkingOut ? "not-allowed" : "pointer",
                transition: "background .15s ease, color .15s ease",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "6px 0 6px" }}>
          <span style={{ fontFamily: "var(--font-display)", fontSize: 44, color: "var(--parchment)", fontWeight: 600 }}>{current.price}</span>
          <span style={{ opacity: 0.7, fontSize: 14 }}>one-time · lifetime access</span>
        </div>
        <p style={{ opacity: 0.7, fontSize: 12.5, marginTop: 0, marginBottom: 14 }}>{current.note}</p>

        {error && <p style={{ color: "var(--coral)", fontSize: 13 }}>{error}</p>}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn-secondary" disabled={checkingOut} onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={checkingOut} onClick={() => onPick(tab)}>
            {checkingOut ? "Redirecting…" : (<><CreditCard size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Pay {current.price} with {current.label}</>)}
          </button>
        </div>
        <p style={{ fontSize: 11.5, opacity: 0.55, marginTop: 12, marginBottom: 0 }}>
          All providers in test/sandbox mode. Stripe test card: 4242 4242 4242 4242 (any future date & CVC).
        </p>
      </div>
    </div>
  );
}
